import React, { useState, useEffect, useMemo } from "react";
import { Plus, Edit2, Trash2, Shield, Copy, X, Code, Search, Loader2 } from "lucide-react";
import api from "../utils/api";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import { useToast } from "../context/ToastContext";

const AiAgents = ({ aiAgents, fetchAiAgents, setActiveAiAgentId }) => {
    const { showToast } = useToast();
    const [isLoading, setIsLoading] = useState(false);
    const [isConfigLoading, setIsConfigLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const userStr = sessionStorage.getItem("user") || localStorage.getItem("user");
    const user = userStr ? JSON.parse(userStr) : null;
    const isSuperAdmin = user?.role === "super_admin";

    // Modal state (Create/Edit Name & Desc)
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingAiAgent, setEditingAiAgent] = useState(null);
    const [formData, setFormData] = useState({ name: "", description: "", is_active: true });

    // Config View Modal state
    const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
    const [configAgent, setConfigAgent] = useState(null);
    const [agentJsonData, setAgentJsonData] = useState(null);

    // Delete confirmation state
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [aiAgentToDelete, setAiAgentToDelete] = useState(null);

    // Billing & usage limits state
    const [billingInfo, setBillingInfo] = useState({
        maxAiAgents: null,
        usedHumanAgentSlots: 0,
        maxHumanAgentSlots: null,
        planName: "Starter"
    });

    // Initial fetch if aiAgents are empty (safety)
    useEffect(() => {
        if (!aiAgents || aiAgents.length === 0) {
            fetchAiAgents();
        }
    }, [aiAgents, fetchAiAgents]);

    useEffect(() => {
        const loadBillingInfo = async () => {
            try {
                const userObj = JSON.parse(sessionStorage.getItem("user") || "{}");
                const tenantId = userObj?.tenant_id || "admin";

                const subRes = await api.request(`/api/subscription/${tenantId}`);
                let maxAiAgents = 0;
                let planName = "No Plan";
                let maxHumanAgentSlots = 0;

                const PLAN_PRESETS = {
                    free_trial: { maxAiAgents: 1, maxHumanAgents: 1 },
                    starter: { maxAiAgents: 3, maxHumanAgents: 3 },
                    growth: { maxAiAgents: 5, maxHumanAgents: 5 },
                    professional: { maxAiAgents: 7, maxHumanAgents: 7 },
                    enterprise: { maxAiAgents: 999, maxHumanAgents: 999 }
                };

                if (subRes && subRes.success && subRes.data) {
                    const planId = subRes.data.plan_id.toLowerCase();
                    planName = subRes.data.plan_name;
                    const config = PLAN_PRESETS[planId] || PLAN_PRESETS.free_trial;
                    maxAiAgents = config.maxAiAgents + (subRes.data.extra_ai_agents || 0);
                    maxHumanAgentSlots = config.maxHumanAgents + (subRes.data.extra_human_agents || 0);
                }

                let usedHumanAgentSlots = 0;
                try {
                    const usersList = await api.request("/api/users");
                    if (Array.isArray(usersList)) {
                        const humanAgentUsers = usersList.filter(u => (u.role === "human_agent" || u.role === "admin") && u.is_active !== false);
                        usedHumanAgentSlots = humanAgentUsers.length;
                    }
                } catch (userErr) {
                    console.error("Failed to load users for counting slots:", userErr);
                }

                setBillingInfo({
                    maxAiAgents,
                    usedHumanAgentSlots,
                    maxHumanAgentSlots,
                    planName
                });
            } catch (err) {
                console.error("Failed to load billing limits for AI Agents page:", err);
            }
        };

        loadBillingInfo();
    }, []);

    const handleOpenModal = (aiAgent = null) => {
        if (aiAgent) {
            setEditingAiAgent(aiAgent);
            setFormData({ name: aiAgent.name, description: aiAgent.description || "", is_active: aiAgent.is_active });
        } else {
            if (billingInfo.maxAiAgents !== null && aiAgents && aiAgents.length >= billingInfo.maxAiAgents) {
                showToast(`You have reached the maximum limit of ${billingInfo.maxAiAgents} AI Agent slots on your current plan. Please upgrade to unlock more slots.`, "error");
                return;
            }
            setEditingAiAgent(null);
            setFormData({ name: "", description: "", is_active: true });
        }
        setIsModalOpen(true);
    };

    const handleOpenConfigModal = async (aiAgent) => {
        setConfigAgent(aiAgent);
        setIsConfigModalOpen(true);
        setIsConfigLoading(true);
        setAgentJsonData(null);

        try {
            const data = await api.request(`/ai-agents/${aiAgent.id}/full-config`);
            setAgentJsonData(data);
        } catch (error) {
            console.error("Error fetching AI agent config:", error);
            showToast("Failed to fetch full configuration schema", "error");
        } finally {
            setIsConfigLoading(false);
        }
    };

    const handleOpenDeleteModal = (aiAgent) => {
        setAiAgentToDelete(aiAgent);
        setIsDeleteModalOpen(true);
    };

    const handleSubmitGeneral = async (e) => {
        e.preventDefault();
        try {
            if (editingAiAgent) {
                await api.request(`/ai-agents/${editingAiAgent.id}`, {
                    method: "PUT",
                    body: JSON.stringify(formData),
                });
                showToast("AI Agent updated successfully");
            } else {
                const res = await api.request("/ai-agents", {
                    method: "POST",
                    body: JSON.stringify(formData),
                });
                showToast("AI Agent created successfully");
                if (res && res.id) {
                    setActiveAiAgentId(res.id);
                }
            }
            setIsModalOpen(false);
            fetchAiAgents();
        } catch (error) {
            console.error("Error saving AI agent:", error);
            const detail = error?.response?.detail || "Failed to save AI Agent";
            showToast(detail, "error");
        }
    };

    const handleDelete = async () => {
        if (!aiAgentToDelete) return;

        setIsLoading(true);
        try {
            await api.request(`/ai-agents/${aiAgentToDelete.id}`, {
                method: "DELETE",
            });
            showToast("AI Agent and all associated data deleted successfully");
            setIsDeleteModalOpen(false);
            setAiAgentToDelete(null);
            fetchAiAgents();
        } catch (error) {
            console.error("Error deleting AI agent:", error);
            showToast("Failed to delete AI Agent", "error");
        } finally {
            setIsLoading(false);
        }
    };

    const toggleAiAgentActive = async (aiAgent) => {
        try {
            await api.request(`/ai-agents/${aiAgent.id}`, {
                method: "PUT",
                body: JSON.stringify({
                    name: aiAgent.name,
                    description: aiAgent.description || "",
                    is_active: !aiAgent.is_active
                }),
            });
            showToast(`AI Agent "${aiAgent.name}" ${!aiAgent.is_active ? 'activated' : 'deactivated'}`);
            fetchAiAgents();
        } catch (error) {
            console.error("Error toggling AI agent active state:", error);
            showToast("Failed to update AI Agent status", "error");
        }
    };

    const filteredAgents = useMemo(() => {
        if (!aiAgents) return [];
        const q = searchQuery.trim().toLowerCase();
        if (!q) return aiAgents;
        return aiAgents.filter(
            (a) =>
                a.name?.toLowerCase().includes(q) ||
                a.id?.toLowerCase().includes(q) ||
                a.description?.toLowerCase().includes(q)
        );
    }, [aiAgents, searchQuery]);

    return (
        <div className="flex flex-col min-h-screen bg-white font-['Inter',sans-serif]">
            <div className="w-full max-w-7xl mx-auto px-4 py-5 sm:px-6 sm:py-6 lg:px-8">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-5 sm:pb-6 border-b border-zinc-100 gap-3 sm:gap-4">
                    <div>
                        <h1 className="text-lg sm:text-xl font-bold text-zinc-900 tracking-tight">AI Agents</h1>
                        <p className="text-xs text-zinc-500 mt-0.5 sm:mt-1">
                            Create and manage isolated AI agents, knowledge bases, and custom tools.
                        </p>
                    </div>
                    {isSuperAdmin && (
                    <button
                        onClick={() => handleOpenModal()}
                        className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 transition-all font-semibold text-xs shadow-xs active:scale-[0.98] self-start sm:self-auto cursor-pointer whitespace-nowrap shrink-0 text-center"
                    >
                        <Plus size={14} strokeWidth={2.5} className="shrink-0" />
                        <span className="whitespace-nowrap text-center">Create AI Agent</span>
                    </button>
                    )}
                </div>

                {/* Minimalist Capacity Metrics */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 pt-5 pb-3">
                    <div className="p-3.5 rounded-xl border border-zinc-200/80 bg-zinc-50/50 flex items-center justify-between">
                        <div>
                            <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">AI Agent Slots</span>
                            <div className="text-base font-bold text-zinc-900 mt-0.5">
                                {aiAgents ? aiAgents.length : 0} <span className="text-zinc-400 font-normal text-xs">/ {billingInfo.maxAiAgents ?? "—"}</span>
                            </div>
                        </div>
                        <span className="text-[10px] font-semibold text-zinc-600 bg-white border border-zinc-200 px-2 py-0.5 rounded-md shadow-2xs">
                            Capacity
                        </span>
                    </div>

                    <div className="p-3.5 rounded-xl border border-zinc-200/80 bg-zinc-50/50 flex items-center justify-between">
                        <div>
                            <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Human Agents</span>
                            <div className="text-base font-bold text-zinc-900 mt-0.5">
                                {billingInfo.usedHumanAgentSlots} <span className="text-zinc-400 font-normal text-xs">/ {billingInfo.maxHumanAgentSlots ?? "—"}</span>
                            </div>
                        </div>
                        <span className="text-[10px] font-semibold text-zinc-600 bg-white border border-zinc-200 px-2 py-0.5 rounded-md shadow-2xs">
                            Active seats
                        </span>
                    </div>

                    <div className="p-3.5 rounded-xl border border-zinc-200/80 bg-zinc-50/50 flex items-center justify-between">
                        <div>
                            <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Plan Tier</span>
                            <div className="text-base font-bold text-zinc-900 mt-0.5">
                                {billingInfo.planName}
                            </div>
                        </div>
                        <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200/60 px-2 py-0.5 rounded-md">
                            Active
                        </span>
                    </div>
                </div>

                {/* Search & Actions Bar */}
                <div className="pt-2 pb-4">
                    <div className="relative w-full sm:max-w-md">
                        <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-zinc-400">
                            <Search size={14} strokeWidth={2.5} />
                        </span>
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search by agent name, ID, or description..."
                            className="w-full text-xs border border-zinc-200 rounded-lg pl-9 pr-3 py-2 focus:border-zinc-900 focus:outline-none bg-white transition-colors text-zinc-800 font-medium placeholder-zinc-400 shadow-2xs"
                        />
                    </div>
                </div>

                {/* Main Content Card */}
                <div className="bg-white rounded-xl border border-zinc-200 shadow-2xs overflow-hidden">
                    {isLoading ? (
                        <div className="flex flex-col items-center justify-center py-20 gap-3">
                            <Loader2 size={24} className="animate-spin text-zinc-400" />
                            <p className="text-xs font-semibold text-zinc-500">Loading AI agents...</p>
                        </div>
                    ) : filteredAgents.length === 0 ? (
                        <div className="p-12 text-center">
              <h3 className="text-sm font-semibold text-zinc-900 mb-1">
                                {searchQuery ? "No matching agents found" : "No AI Agents Yet"}
                            </h3>
                            <p className="text-xs text-zinc-500 max-w-sm mx-auto">
                                {searchQuery
                                    ? `No agents match "${searchQuery}". Clear your search query.`
                                    : "Create your first AI agent to start configuring prompts, training knowledge, and tools."}
                            </p>
                        </div>
                    ) : (
                        <>
                            {/* Mobile Card List (visible on screens < md) */}
                            <div className="block md:hidden divide-y divide-zinc-100">
                                {filteredAgents.map((aiAgent) => {
                                    const isProtectedAiAgent =
                                        aiAgent.id === 'b8d8cf9e-3b2d-4f10-91a5-e3d81b2f7c03' ||
                                        aiAgent.id === user?.ai_agent_id ||
                                        aiAgents.length === 1;

                                    return (
                                        <div key={aiAgent.id} className="p-4 space-y-3">
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="flex items-center gap-2">
                                                    <h4 className="font-bold text-zinc-900 text-sm">{aiAgent.name}</h4>
                                                    <button
                                                        onClick={() => handleOpenModal(aiAgent)}
                                                        className="text-zinc-400 hover:text-zinc-900 p-1 rounded hover:bg-zinc-100"
                                                        title="Edit Agent Info"
                                                    >
                                                        <Edit2 size={12} />
                                                    </button>
                                                </div>
                                                <div className="flex items-center gap-2 shrink-0">
                                                    {isProtectedAiAgent ? (
                                                        <span className="text-[10px] font-bold text-zinc-500 bg-zinc-100 px-2 py-0.5 rounded border border-zinc-200">
                                                            Protected
                                                        </span>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            onClick={() => toggleAiAgentActive(aiAgent)}
                                                            title={aiAgent.is_active ? "Deactivate AI Agent" : "Activate AI Agent"}
                                                            className={`w-8 h-4.5 rounded-full transition-colors relative flex-shrink-0 cursor-pointer ${
                                                                aiAgent.is_active ? "bg-zinc-900" : "bg-zinc-200"
                                                            }`}
                                                        >
                                                            <div
                                                                className={`absolute top-0.5 w-3.5 h-3.5 bg-white rounded-full transition-transform shadow-xs ${
                                                                    aiAgent.is_active ? "left-4" : "left-0.5"
                                                                }`}
                                                            />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>

                                            {aiAgent.description && (
                                                <p className="text-xs text-zinc-500 leading-relaxed line-clamp-2">
                                                    {aiAgent.description}
                                                </p>
                                            )}

                                            <div className="flex flex-col gap-2 pt-1">
                                                <div className="flex items-center justify-between text-[11px] bg-zinc-50 p-2 rounded-lg border border-zinc-100">
                                                    <span className="text-zinc-400 font-medium">ID:</span>
                                                    <div className="flex items-center gap-1.5 font-mono text-zinc-700">
                                                        <span className="truncate max-w-[180px]">{aiAgent.id}</span>
                                                        <button
                                                            onClick={() => {
                                                                navigator.clipboard.writeText(aiAgent.id);
                                                                showToast("AI Agent ID copied to clipboard");
                                                            }}
                                                            className="p-1 text-zinc-400 hover:text-zinc-900"
                                                        >
                                                            <Copy size={11} />
                                                        </button>
                                                    </div>
                                                </div>

                                                {aiAgent.ai_agent_key && (
                                                    <div className="flex items-center justify-between text-[11px] bg-zinc-50 p-2 rounded-lg border border-zinc-100">
                                                        <span className="text-zinc-400 font-medium">Key:</span>
                                                        <div className="flex items-center gap-1.5 font-mono text-zinc-700">
                                                            <span className="truncate max-w-[180px]">{aiAgent.ai_agent_key}</span>
                                                            <button
                                                                onClick={() => {
                                                                    navigator.clipboard.writeText(aiAgent.ai_agent_key);
                                                                    showToast("AI Agent Key copied to clipboard");
                                                                }}
                                                                className="p-1 text-zinc-400 hover:text-zinc-900"
                                                            >
                                                                <Copy size={11} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-100">
                                                <button
                                                    onClick={() => handleOpenConfigModal(aiAgent)}
                                                    className="px-2.5 py-1.5 text-zinc-600 bg-zinc-100 hover:bg-zinc-200 rounded-lg text-xs font-semibold flex items-center gap-1"
                                                >
                                                    <Code size={13} />
                                                    <span>Schema</span>
                                                </button>
                                                {!isProtectedAiAgent && (
                                                    <button
                                                        onClick={() => handleOpenDeleteModal(aiAgent)}
                                                        className="px-2.5 py-1.5 text-rose-600 bg-rose-50 hover:bg-rose-100 rounded-lg text-xs font-semibold flex items-center gap-1"
                                                    >
                                                        <Trash2 size={13} />
                                                        <span>Delete</span>
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Desktop Table (visible on screens >= md) */}
                            <div className="hidden md:block overflow-x-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="border-b border-zinc-200 bg-zinc-50/50">
                                            <th className="px-4 py-3 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Name</th>
                                            <th className="px-4 py-3 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Agent ID</th>
                                            <th className="px-4 py-3 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Agent Key</th>
                                            <th className="px-4 py-3 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Description</th>
                                            <th className="px-4 py-3 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider text-center">Status</th>
                                            <th className="px-4 py-3 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider text-right pr-6">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-zinc-100 text-xs">
                                        {filteredAgents.map((aiAgent) => {
                                            const isProtectedAiAgent =
                                                aiAgent.id === 'b8d8cf9e-3b2d-4f10-91a5-e3d81b2f7c03' ||
                                                aiAgent.id === user?.ai_agent_id ||
                                                aiAgents.length === 1;

                                            return (
                                                <tr key={aiAgent.id} className="hover:bg-zinc-50/70 transition-colors group">
                                                    <td className="px-4 py-3.5 font-semibold text-zinc-900 align-middle">
                                                        <div className="flex items-center gap-2">
                                                            <span>{aiAgent.name}</span>
                                                            <button
                                                                onClick={() => handleOpenModal(aiAgent)}
                                                                className="text-zinc-400 hover:text-zinc-900 transition-colors p-1 rounded hover:bg-zinc-100"
                                                                title="Edit Agent Info"
                                                            >
                                                                <Edit2 size={12} />
                                                            </button>
                                                        </div>
                                                    </td>

                                                    <td className="px-4 py-3.5 align-middle">
                                                        <div className="flex items-center gap-1.5">
                                                            <span className="font-mono text-[11px] text-zinc-600 bg-zinc-100/80 px-2 py-0.5 rounded border border-zinc-200/60 max-w-[120px] truncate">
                                                                {aiAgent.id}
                                                            </span>
                                                            <button
                                                                onClick={() => {
                                                                    navigator.clipboard.writeText(aiAgent.id);
                                                                    showToast("AI Agent ID copied to clipboard");
                                                                }}
                                                                className="p-1 text-zinc-400 hover:text-zinc-900 transition-colors rounded hover:bg-zinc-100"
                                                                title="Copy Agent ID"
                                                            >
                                                                <Copy size={12} />
                                                            </button>
                                                        </div>
                                                    </td>

                                                    <td className="px-4 py-3.5 align-middle">
                                                        <div className="flex items-center gap-1.5">
                                                            <span className="font-mono text-[11px] text-zinc-600 bg-zinc-100/80 px-2 py-0.5 rounded border border-zinc-200/60 max-w-[130px] truncate">
                                                                {aiAgent.ai_agent_key || "GENERATING..."}
                                                            </span>
                                                            {aiAgent.ai_agent_key && (
                                                                <button
                                                                    onClick={() => {
                                                                        navigator.clipboard.writeText(aiAgent.ai_agent_key);
                                                                        showToast("AI Agent Key copied to clipboard");
                                                                    }}
                                                                    className="p-1 text-zinc-400 hover:text-zinc-900 transition-colors rounded hover:bg-zinc-100"
                                                                    title="Copy Agent Key"
                                                                >
                                                                    <Copy size={12} />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>

                                                    <td className="px-4 py-3.5 text-zinc-500 max-w-[200px] truncate align-middle">
                                                        {aiAgent.description || "—"}
                                                    </td>

                                                    <td className="px-4 py-3.5 align-middle text-center">
                                                        <div className="flex items-center justify-center">
                                                            {isProtectedAiAgent ? (
                                                                <span className="text-[10px] font-bold text-zinc-500 bg-zinc-100 px-2 py-0.5 rounded border border-zinc-200" title="System AI Agent is permanently active">
                                                                    Protected
                                                                </span>
                                                            ) : (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => toggleAiAgentActive(aiAgent)}
                                                                    title={aiAgent.is_active ? "Deactivate AI Agent" : "Activate AI Agent"}
                                                                    className={`w-8 h-4.5 rounded-full transition-colors relative flex-shrink-0 cursor-pointer focus:outline-none ${
                                                                        aiAgent.is_active ? "bg-zinc-900" : "bg-zinc-200 hover:bg-zinc-300"
                                                                    }`}
                                                                >
                                                                    <div
                                                                        className={`absolute top-0.5 w-3.5 h-3.5 bg-white rounded-full transition-transform shadow-xs ${
                                                                            aiAgent.is_active ? "left-4" : "left-0.5"
                                                                        }`}
                                                                    />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>

                                                    <td className="px-4 py-3.5 align-middle text-right pr-6">
                                                        <div className="flex items-center justify-end gap-1.5">
                                                            <button
                                                                onClick={() => handleOpenConfigModal(aiAgent)}
                                                                className="p-1.5 text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 rounded-lg transition-colors border border-transparent hover:border-zinc-200"
                                                                title="View Config JSON"
                                                            >
                                                                <Code size={14} />
                                                            </button>
                                                            {isProtectedAiAgent ? (
                                                                <div
                                                                    className="p-1.5 text-zinc-300 cursor-help"
                                                                    title="Protected AI Agent — cannot be deleted"
                                                                >
                                                                    <Shield size={14} />
                                                                </div>
                                                            ) : (
                                                                <button
                                                                    onClick={() => handleOpenDeleteModal(aiAgent)}
                                                                    className="p-1.5 text-zinc-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                                                                    title="Delete AI Agent"
                                                                >
                                                                    <Trash2 size={14} />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* General Edit / Create Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-zinc-900/40 backdrop-blur-sm flex items-center justify-center z-[200] p-4">
                    <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-xl border border-zinc-200">
                        <div className="px-6 py-4 border-b border-zinc-100 flex justify-between items-center">
                            <div>
                                <h3 className="text-sm font-bold text-zinc-900">
                                    {editingAiAgent ? "Edit AI Agent" : "Create New AI Agent"}
                                </h3>
                                <p className="text-[11px] text-zinc-500 mt-0.5">Define agent title and workspace details</p>
                            </div>
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="p-1 text-zinc-400 hover:text-zinc-900 rounded-lg transition-colors"
                            >
                                <X size={16} />
                            </button>
                        </div>
                        <form onSubmit={handleSubmitGeneral} className="p-6 space-y-4">
                            <div className="space-y-1">
                                <label className="text-[11px] font-semibold text-zinc-700">Agent Name</label>
                                <input
                                    type="text"
                                    required
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="w-full px-3 py-2 bg-white border border-zinc-200 rounded-lg focus:outline-none focus:border-zinc-900 text-zinc-900 text-xs transition-colors"
                                    placeholder="e.g. Support AI Agent"
                                />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[11px] font-semibold text-zinc-700">Description</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    className="w-full px-3 py-2 bg-white border border-zinc-200 rounded-lg focus:outline-none focus:border-zinc-900 text-zinc-900 text-xs transition-colors resize-none"
                                    placeholder="Brief note regarding this AI agent..."
                                    rows={3}
                                />
                            </div>

                            <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-100">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="px-4 py-2 text-zinc-600 hover:text-zinc-900 font-semibold text-xs transition-colors rounded-lg bg-zinc-100 hover:bg-zinc-200"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-zinc-900 text-white font-semibold text-xs rounded-lg hover:bg-zinc-800 transition-all shadow-xs"
                                >
                                    {editingAiAgent ? "Save Changes" : "Create Agent"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Config JSON View Modal */}
            {isConfigModalOpen && (
                <div className="fixed inset-0 bg-zinc-900/40 backdrop-blur-sm flex items-center justify-center z-[200] p-4">
                    <div className="bg-white rounded-2xl w-full max-w-2xl overflow-hidden shadow-xl border border-zinc-200 flex flex-col h-[70vh]">
                        {/* Header */}
                        <div className="px-6 py-4 border-b border-zinc-100 flex justify-between items-center">
                            <div>
                                <h3 className="text-sm font-bold text-zinc-900">
                                    Agent JSON Config: {configAgent?.name}
                                </h3>
                                <p className="text-[11px] text-zinc-500 mt-0.5">Direct snapshot of the database configuration</p>
                            </div>
                            <button
                                onClick={() => setIsConfigModalOpen(false)}
                                className="p-1 text-zinc-400 hover:text-zinc-900 rounded-lg transition-colors"
                            >
                                <X size={16} />
                            </button>
                        </div>

                        {/* Body */}
                        <div className="flex-1 p-6 overflow-hidden relative flex flex-col">
                            {isConfigLoading ? (
                                <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex flex-col items-center justify-center gap-2">
                                    <Loader2 size={20} className="animate-spin text-zinc-400" />
                                    <p className="text-xs text-zinc-500 font-medium">Compiling configuration...</p>
                                </div>
                            ) : agentJsonData ? (
                                <div className="flex-1 flex flex-col gap-3 min-h-0">
                                    <div className="flex justify-between items-center flex-shrink-0">
                                        <span className="text-[11px] font-semibold text-zinc-500">JSON Schema</span>
                                        <button
                                            onClick={() => {
                                                navigator.clipboard.writeText(JSON.stringify(agentJsonData, null, 2));
                                                showToast("JSON Config copied to clipboard");
                                            }}
                                            className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-900 text-white hover:bg-zinc-800 text-xs font-semibold rounded-lg transition-all shadow-xs"
                                        >
                                            <Copy size={12} />
                                            Copy JSON
                                        </button>
                                    </div>
                                    <div className="flex-1 bg-zinc-950 rounded-xl p-4 border border-zinc-900 overflow-auto custom-scrollbar">
                                        <pre className="text-[11px] font-mono text-zinc-300 leading-relaxed whitespace-pre-wrap select-all">
                                            {JSON.stringify(agentJsonData, null, 2)}
                                        </pre>
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-10 text-xs font-medium text-zinc-400">
                                    Failed to retrieve config schema
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <DeleteConfirmModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleDelete}
                isLoading={isLoading}
                title="Delete AI Agent?"
                message={`This will permanently delete '${aiAgentToDelete?.name}' and all associated knowledge, configurations, and chat sessions.`}
            />
        </div>
    );
};

export default AiAgents;
